/**
 * Primitivos de UI do módulo.
 *
 * ⚠️ **Nenhum valor de design é declarado aqui.** Toda cor, raio, sombra e
 * espaçamento vem de `styles/design-system/tokens/*.css`, através dos utilitários
 * do Tailwind. Se algo precisa mudar, muda lá — o design system é fixo
 * (PROJECT-OVERVIEW §8).
 *
 * ⚠️ **Sem shadcn/ui ainda, e é uma escolha.** O `CLAUDE.md` diz para não rodar
 * `shadcn init` preventivamente e puxar um primitivo só quando algo precisar de
 * comportamento Radix — teclado complexo, foco preso, portal. Nada nesta rodada
 * precisa: os selects são `<select>` nativos (que já vêm com teclado, busca por
 * digitação e acessibilidade de graça), o switch é um `<button role="switch">` e
 * o drawer é um `<dialog>`. O gatilho para Radix será o menu de filtro por
 * coluna da `2e` se ele virar combobox com busca.
 */
import type { ReactNode } from 'react'

type Tom = 'neutro' | 'info' | 'sucesso' | 'atencao' | 'perigo'

const TOM: Record<Tom, string> = {
  neutro: 'bg-gray-100 text-text-body',
  info: 'bg-info-100 text-info-700',
  sucesso: 'bg-success-100 text-success-700',
  atencao: 'bg-warning-100 text-warning-700',
  perigo: 'bg-danger-100 text-danger-700',
}

/**
 * Mapa status -> tom, transcrito do shot `2e`.
 *
 * ⚠️ Status DESCONHECIDO cai em `neutro`, e nunca lança. O catálogo é aberto
 * (BR-24): um Administrador pode ter criado "em negociação" hoje de manhã, e a
 * tela não pode quebrar por isso. Um `Record` exaustivo com `switch` seria o
 * erro exato que a regra existe para prevenir.
 */
const TOM_DO_STATUS: Record<string, Tom> = {
  rascunho: 'neutro',
  nova: 'info',
  proposta: 'info',
  priorizada: 'atencao',
  implementada: 'sucesso',
  arquivada: 'neutro',
}

export function Badge({ children, tom = 'neutro' }: { children: ReactNode; tom?: Tom }) {
  return (
    <span className={`rounded-control px-2.5 py-1 type-eyebrow whitespace-nowrap ${TOM[tom]}`}>
      {children}
    </span>
  )
}

export function BadgeDeStatus({ status, rotulo }: { status: string; rotulo?: string | undefined }) {
  return <Badge tom={TOM_DO_STATUS[status] ?? 'neutro'}>{rotulo ?? status}</Badge>
}

/**
 * Um dos cards de métrica das telas `2g` e `2h`.
 *
 * `caption` e `delta` são posições distintas no shot: a legenda explica o que o
 * número é; o delta qualifica o movimento. Misturar os dois faria o card mentir
 * sobre variação onde só há explicação.
 */
export function MetricCard({
  rotulo,
  valor,
  caption,
  delta,
  tomDoDelta = 'neutro',
  grande = false,
  negativo = false,
}: {
  rotulo: string
  valor: string
  caption?: string
  delta?: string
  tomDoDelta?: 'neutro' | 'positivo'
  grande?: boolean
  /** Valor em tom de alerta — "divergências com a IA" de `3b`. */
  negativo?: boolean
}) {
  return (
    <div
      className={`border-border-subtle bg-surface-card rounded-card flex flex-col justify-between border p-5 ${
        grande ? 'min-h-[110px]' : 'min-h-[96px]'
      }`}
    >
      <span className="type-eyebrow text-text-muted">{rotulo}</span>
      <span className={`${negativo ? 'text-danger-700' : 'text-text-primary'} ${grande ? 'type-h2' : 'type-h3'}`}>
        {valor}
      </span>
      {caption && (
        <span className={`type-sm ${negativo ? 'text-danger-700 font-semibold' : 'text-text-muted'}`}>{caption}</span>
      )}
      {delta && (
        <span
          className={`type-sm ${tomDoDelta === 'positivo' ? 'text-success-700' : 'text-text-muted'}`}
        >
          {delta}
        </span>
      )}
    </div>
  )
}

/** Chip removível — os do "Agrupar por" da `2c` e os de filtro ativo da `2e`. */
export function Chip({
  children,
  aoRemover,
  rotuloDeRemocao,
}: {
  children: ReactNode
  aoRemover?: () => void
  rotuloDeRemocao?: string
}) {
  return (
    <span className="rounded-control bg-iox-navy text-text-inverse inline-flex items-center gap-2 px-3 py-1.5 type-sm">
      {children}
      {aoRemover && (
        <button
          type="button"
          onClick={aoRemover}
          // Rótulo específico, e não um "remover" genérico: num leitor de tela,
          // cinco botões "remover" seguidos são indistinguíveis.
          aria-label={rotuloDeRemocao ?? 'remover'}
          className="text-text-inverse/70 hover:text-text-inverse duration-fast"
        >
          <i className="ph ph-x" aria-hidden="true" />
        </button>
      )}
    </span>
  )
}

/**
 * O toggle "Visível cliente" da `2e`.
 *
 * 🔒 `role="switch"` + `aria-checked`, e não um checkbox: semanticamente isto é
 * um estado que muda imediatamente, e não uma escolha a ser submetida. Um leitor
 * de tela anuncia "ativado/desativado" em vez de "marcado".
 *
 * `desabilitado` é a metade de UX do BR-17 — o servidor recusa a escrita de
 * qualquer forma; desabilitar o controle é cortesia, não segurança.
 */
export function Switch({
  ligado,
  aoAlternar,
  rotulo,
  desabilitado = false,
  ocupado = false,
}: {
  ligado: boolean
  aoAlternar: (proximo: boolean) => void
  rotulo: string
  desabilitado?: boolean
  ocupado?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      disabled={desabilitado || ocupado}
      onClick={() => aoAlternar(!ligado)}
      className={`rounded-control relative inline-flex h-6 w-11 shrink-0 items-center transition-colors duration-fast ${
        ligado ? 'bg-iox-navy' : 'bg-gray-300'
      } ${desabilitado ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      <span
        className={`rounded-control h-5 w-5 bg-white shadow transition-transform duration-fast ${
          ligado ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}

export function Campo({
  rotulo,
  hint,
  obrigatorio = false,
  erro,
  children,
  className = '',
}: {
  rotulo: string
  // `| undefined` explícito por causa de `exactOptionalPropertyTypes`: sem ele,
  // passar `erro={talvezUndefined}` não compila, e a alternativa seria montar o
  // objeto de props condicionalmente em toda chamada.
  hint?: string | undefined
  obrigatorio?: boolean | undefined
  erro?: string | undefined
  children: ReactNode
  className?: string | undefined
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="type-label text-text-primary">
        {rotulo}
        {obrigatorio && <span className="text-danger-700"> *</span>}
      </span>
      {children}
      {erro ? (
        <span className="type-sm text-danger-700">{erro}</span>
      ) : (
        hint && <span className="type-sm text-text-muted">{hint}</span>
      )}
    </label>
  )
}

const CLASSE_DE_CONTROLE =
  'rounded-field border-border-subtle bg-surface-card text-text-primary type-body ' +
  'w-full border px-3 py-2 duration-fast disabled:bg-gray-50 disabled:text-text-muted'

export function Entrada(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CLASSE_DE_CONTROLE} ${props.className ?? ''}`} />
}

export function AreaDeTexto(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`${CLASSE_DE_CONTROLE} min-h-[52px] ${props.className ?? ''}`}
    />
  )
}

/**
 * 🔒 BR-24: todo dropdown do módulo é alimentado por CATÁLOGO, nunca por texto
 * livre. `opcoes` vem sempre de uma query — se alguma tela passar um array
 * literal, é sinal de que falta o catálogo dela.
 *
 * `<select>` nativo, e não um combobox: ele já traz teclado, busca por digitação
 * e acessibilidade prontos. Só vale trocar por Radix quando algum campo precisar
 * de busca em lista longa.
 */
export function Selecao({
  opcoes,
  placeholder = 'selecione…',
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  opcoes: { valor: string; rotulo: string }[]
  placeholder?: string
}) {
  return (
    <select {...props} className={`${CLASSE_DE_CONTROLE} ${props.className ?? ''}`}>
      <option value="">{placeholder}</option>
      {opcoes.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.rotulo}
        </option>
      ))}
    </select>
  )
}

type Variante = 'primaria' | 'secundaria' | 'accent' | 'discreta'

const VARIANTE: Record<Variante, string> = {
  // Preto -> navy no hover, como o handoff especifica. Press muda a COR, e nunca
  // aplica scale.
  primaria: 'bg-action-primary text-action-primary-fg hover:bg-action-primary-hover',
  secundaria:
    'border border-border-subtle bg-surface-card text-text-primary hover:border-border-strong',
  accent: 'bg-iox-navy text-text-inverse hover:bg-navy-800',
  discreta: 'text-text-link hover:text-iox-teal',
}

/** Copy dos botões: minúscula e verbo primeiro — "salvar oportunidade". */
export function Botao({
  variante = 'secundaria',
  icone,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; icone?: string }) {
  return (
    <button
      type="button"
      {...props}
      className={`rounded-control inline-flex items-center justify-center gap-2 px-4 py-2 type-button duration-fast disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTE[variante]} ${props.className ?? ''}`}
    >
      {icone && <i className={`ph ph-${icone}`} aria-hidden="true" />}
      {children}
    </button>
  )
}
